// Friends (app flow §6): four tabs (your friends, requests with a count, find by handle, your invite
// link). Rows on hairlines, one decision on the right. An answered request slides out of the list.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useDeferredValue, useState } from "react";
import { Avatar } from "../../components/Avatar";
import { AvatarStack } from "../../components/AvatarStack";
import { Button, LinkButton } from "../../components/Button";
import { EmptyState } from "../../components/Empty";
import { Field, Input } from "../../components/Field";
import { Icon } from "../../components/Icon";
import { SectionHeader } from "../../components/SectionHeader";
import { Surface } from "../../components/Surface";
import { TabPanel, Tabs } from "../../components/Tabs";
import ui from "../../components/ui.module.css";
import { useFriendAction, useFriends, usePeople, useSession } from "../../data/queries";
import type { UserCard } from "../../data/types";
import { useTitle } from "../../lib/useTitle";
import { AnimatedList, morphFrom } from "../../motion";
import s from "./friends.module.css";

type Tab = "friends" | "requests" | "find" | "invite";
const TABS: Tab[] = ["friends", "requests", "find", "invite"];

export const Route = createFileRoute("/_app/friends")({
  validateSearch: (q: Record<string, unknown>): { tab?: Tab } => (TABS.includes(q.tab as Tab) && q.tab !== "friends" ? { tab: q.tab as Tab } : {}),
  component: FriendsPage,
});

function Who({ user }: { user: UserCard }) {
  return (
    <Link to="/@{$handle}" params={{ handle: user.handle }} className={ui.person} onClick={morphFrom("avatar", user.id)}>
      <Avatar user={user} size={44} morph />
      <span className={s.names}>
        <span className={ui.personName}>{user.displayName}</span>
        <span className={ui.personHandle}>@{user.handle}</span>
      </span>
    </Link>
  );
}

const names = (users: UserCard[]) => {
  const first = users.slice(0, 2).map((u) => u.displayName.split(" ")[0]);
  const rest = users.length - first.length;
  return rest > 0 ? `${first.join(", ")} and ${rest} more` : first.join(" and ");
};

function FriendsPage() {
  const { tab = "friends" } = Route.useSearch();
  const navigate = useNavigate();
  const { data } = useFriends();
  const act = useFriendAction();
  useTitle("Friends");
  const incoming = data?.incoming.length ?? 0;
  const friends = data?.friends ?? [];

  return (
    <div className={ui.page}>
      <header className={`${ui.pageHead} ${s.head}`}>
        <div>
          <h1 className={ui.title}>Friends</h1>
          {friends.length > 0 && (
            <p className={s.together}>
              <AvatarStack users={friends} max={5} size={28} />
              <span>{names(friends)}</span>
            </p>
          )}
        </div>
      </header>
      <Tabs
        label="Friends"
        idPrefix="friends"
        value={tab}
        onChange={(t) => void navigate({ to: "/friends", search: t === "friends" ? {} : { tab: t }, replace: true })}
        items={[
          { id: "friends", label: "Friends" },
          { id: "requests", label: "Requests", count: incoming },
          { id: "find", label: "Find" },
          { id: "invite", label: "Invite" },
        ]}
      />

      <TabPanel idPrefix="friends" value={tab} index={TABS.indexOf(tab)} className={s.panel}>
        {tab === "friends" &&
          (friends.length > 0 ? (
            <AnimatedList className={s.list} aria-label="Your friends">
              {friends.map((u) => (
                <li key={u.id}>
                  <Who user={u} />
                </li>
              ))}
            </AnimatedList>
          ) : (
            <EmptyState icon="people" title="No friends yet." body="Fellowship is for the people you already know. Find them by handle, or send your invite link.">
              <LinkButton to="/friends" search={{ tab: "find" }} variant="primary">
                Find people
              </LinkButton>
              <LinkButton to="/friends" search={{ tab: "invite" }} variant="secondary">
                Invite
              </LinkButton>
            </EmptyState>
          ))}

        {tab === "requests" && data && (
          <>
            <section aria-labelledby="in-head">
              <SectionHeader id="in-head" title="Asking to be friends" className={s.sectionHead} />
              {data.incoming.length ? (
                <AnimatedList className={s.list}>
                  {data.incoming.map((u) => (
                    <li key={u.id}>
                      <Who user={u} />
                      <span className={s.decide}>
                        <Button variant="ghost" size="sm" onClick={() => act.mutate({ action: "decline", userId: u.id })}>
                          Decline
                        </Button>
                        <Button variant="primary" size="sm" onClick={() => act.mutate({ action: "accept", userId: u.id })}>
                          Accept
                        </Button>
                      </span>
                    </li>
                  ))}
                </AnimatedList>
              ) : (
                <p className={s.quiet}>No one is waiting.</p>
              )}
            </section>
            <section aria-labelledby="out-head" className={s.section}>
              <SectionHeader id="out-head" title="You asked" className={s.sectionHead} />
              {data.outgoing.length ? (
                <AnimatedList className={s.list}>
                  {data.outgoing.map((u) => (
                    <li key={u.id}>
                      <Who user={u} />
                      <Button variant="ghost" size="sm" onClick={() => act.mutate({ action: "decline", userId: u.id })}>
                        Cancel
                      </Button>
                    </li>
                  ))}
                </AnimatedList>
              ) : (
                <p className={s.quiet}>Nothing pending.</p>
              )}
            </section>
          </>
        )}

        {tab === "find" && <Find />}
        {tab === "invite" && <Invite />}
      </TabPanel>
    </div>
  );
}

function Find() {
  const [q, setQ] = useState("");
  const query = useDeferredValue(q);
  const { data: people, isFetching } = usePeople(query);
  const act = useFriendAction();

  return (
    <div className={s.find}>
      <Field label="Search by @handle" hint="Only handles are searchable. Names and emails never are.">
        {(p) => <Input {...p} type="search" placeholder="@ruth" autoComplete="off" spellCheck={false} value={q} onChange={(e) => setQ(e.target.value)} />}
      </Field>
      <ul className={s.list} aria-busy={isFetching} aria-label="People">
        {people?.map((u) => (
          <li key={u.id}>
            <Who user={u} />
            {u.relation === "none" ? (
              <Button variant="secondary" size="sm" icon="user-plus" onClick={() => act.mutate({ action: "request", userId: u.id, handle: u.handle })}>
                Add friend
              </Button>
            ) : (
              <span className={s.state}>{u.relation === "friends" ? "Friends" : u.relation === "requested" ? "Requested" : "Wants to be friends"}</span>
            )}
          </li>
        ))}
      </ul>
      {query.trim() && people?.length === 0 && <p className={s.quiet}>No one with that handle.</p>}
      {!query.trim() && (
        <p className={s.quiet}>
          Not on Fellowship yet?{" "}
          <Link to="/friends" search={{ tab: "invite" }} className={s.inline}>
            Send your invite link
          </Link>
          .
        </p>
      )}
    </div>
  );
}

function Invite() {
  const { data: session } = useSession();
  const [copied, setCopied] = useState(false);
  const invite = `${location.origin}/i/${session?.inviteCode ?? session?.user.handle ?? ""}`;
  const canShare = typeof navigator !== "undefined" && "share" in navigator;

  return (
    <Surface as="section" variant="panel" pad="lg" className={s.invite} aria-labelledby="invite-head">
      <h2 id="invite-head" className={s.inviteTitle}>
        Your invite link
      </h2>
      <p className={s.inviteBody}>Anyone who opens it asks to be your friend. You still choose who to accept.</p>
      <p className={s.link}>
        <Icon name="link" size={16} />
        <span>{invite}</span>
      </p>
      <div className={s.inviteActions}>
        <Button
          variant="primary"
          icon={copied ? "check" : "link"}
          onClick={() => {
            void navigator.clipboard?.writeText(invite);
            setCopied(true);
          }}
        >
          {copied ? "Copied" : "Copy link"}
        </Button>
        {canShare && (
          <Button variant="secondary" icon="share" onClick={() => void navigator.share({ title: "Read with me on Fellowship", url: invite }).catch(() => undefined)}>
            Share
          </Button>
        )}
      </div>
      <span className="visually-hidden" aria-live="polite">
        {copied ? "Invite link copied" : ""}
      </span>
    </Surface>
  );
}
